// Global Instructions Rule Applied! - Mar 16, 2025
import React, { useState, type MouseEvent } from 'react'
import { Dropdown, Avatar, Popconfirm } from 'antd'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../state_manager/hooks/useAppDispatch.ts'
import type { RootState } from '../../../state_manager/store.ts'
import { useNavigate } from 'react-router-dom'
import { performLogout } from '../../auth/authService'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faSignOutAlt, faBars } from '@fortawesome/free-solid-svg-icons'
import { portalGradients } from '../../../portal-configs/colors'
import { NavbarSearch } from './NavbarSearch.tsx'
import StageAlertsBell from '../../../app_modules/stage_alerts/components/StageAlertsBell.tsx'

export function Navbar({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [popconfirmOpen, setPopconfirmOpen] = useState(false)

  const handleConfirmLogout = () => {
    setPopconfirmOpen(false)
    setDropdownOpen(false)
    performLogout({
      dispatch,
      navigate,
      onSuccess: () => {
        // Logout successful
      },
      onError: (error: unknown) => {
        console.error('Logout failed:', error)
      }
    })
  }

  const handlePopconfirmCancel = (e?: MouseEvent<HTMLElement>) => {
    e?.stopPropagation()
    setPopconfirmOpen(false)
  }

  const handlePopconfirmOpenChange = (open: boolean) => {
    setPopconfirmOpen(open)
  }

  const handleDropdownOpenChange = (open: boolean) => {
    // Don't close dropdown if Popconfirm is open
    if (!open && popconfirmOpen) {
      // Prevent closing when Popconfirm is visible
      return
    }
    setDropdownOpen(open)
    // If opening and Popconfirm was open, keep it open
    if (open && popconfirmOpen) {
      setPopconfirmOpen(true)
    }
  }

  // Get user initials from name or email
  const getUserInitials = () => {
    if (user?.name) {
      return user.name
        .split(' ')
        .map((name) => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2) // Limit to 2 characters
    } else if (user?.email) {
      return user.email.charAt(0).toUpperCase()
    }
    return 'U'
  }
  //Format user name with proper capitalization
  const formatUserName = () => {
    if (user?.name) {
      return user.name
        .split(' ')
        .map((name) => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase())
        .join(' ')
    }
    return user?.email || 'User'
  }

  const userMenuItems = [
    {
      key: 'user-info',
      label: (
        <div className='flex items-center gap-2 py-2 border-b border-gray-200 max-w-80 min-w-44'>
          <FontAwesomeIcon icon={faUser} className='text-blue-500 flex-shrink-0' />
          <div className='min-w-0 flex-1'>
            <p className='font-semibold text-black whitespace-nowrap'>Signed in as</p>
            {user?.name && <p className='font-semibold text-black whitespace-nowrap'>{formatUserName()}</p>}
            <p className='text-sm text-black whitespace-nowrap'>{user?.email}</p>
          </div>
        </div>
      ),
      disabled: true
    },
    {
      key: 'logout',
      label: (
        <Popconfirm
          title='Are you sure you want to sign out?'
          onConfirm={handleConfirmLogout}
          onCancel={handlePopconfirmCancel}
          onOpenChange={handlePopconfirmOpenChange}
          open={popconfirmOpen}
          destroyTooltipOnHide
          okText='Sign Out'
          cancelText='Cancel'
          okButtonProps={{ danger: true }}
          placement='bottom'
        >
          <div
            className='flex items-center gap-2 text-red-600 min-w-44 cursor-pointer'
            onClick={(e) => {
              e.stopPropagation()
              setPopconfirmOpen(true)
            }}
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span className='whitespace-nowrap'>Sign Out</span>
          </div>
        </Popconfirm>
      )
    }
  ]

  return (
    <div
      className='w-full shadow-lg h-10 relative flex items-center justify-between pr-4 z-50 modal-buttons'
      style={{
        background: portalGradients.navbar
      }}
    >
      {/* Left side - Burger menu */}
      <div className='flex items-center'>
        <button
          onClick={onToggleSidebar}
          className='p-1.5 ml-2 rounded-lg transition-colors hover:bg-white hover:bg-opacity-10 cursor-pointer'
          aria-label='Toggle sidebar'
        >
          <FontAwesomeIcon icon={faBars} className='text-white text-lg' />
        </button>
      </div>

      {/* Center - Search select with dropdown (visible when signed in) */}
      {isAuthenticated && <NavbarSearch />}

      {/* Right side - Stage-alerts bell + user icon */}
      {isAuthenticated && (
        <div className='flex items-center gap-4'>
          <StageAlertsBell />
          <Dropdown
            menu={{ items: userMenuItems }}
            placement='bottomRight'
            trigger={['hover']}
            overlayStyle={{ minWidth: '176px', maxWidth: '320px' }}
            open={dropdownOpen}
            onOpenChange={handleDropdownOpenChange}
          >
            <Avatar
              style={{
                backgroundColor: '#FFFFFF',
                color: '#2887D0',
                border: '2px solid #FFFFFF',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '800',
                width: '28px',
                height: '28px',
                borderRadius: '4px'
              }}
              size={28}
            >
              {getUserInitials()}
            </Avatar>
          </Dropdown>
        </div>
      )}
    </div>
  )
}
